import { range } from 'ramda'
import React from 'react'
import moment from 'moment'

import { calculateWidthAndPositionForConflicts } from './_helpers/conflict'
import { useEffectAfterMount } from '../../utils/helpers/effectAfterMount'

const CalendarContext = React.createContext()
export const useCalendar = () => React.useContext(CalendarContext)

const SQUARES = range(1, 43)
export const WEEK_DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sabado', 'Domingo']

const CalendarHandler = ({
  children,
  events: initialEvents,
  onChangeDate,
  onSelectEmptyTime,
  onSelectEvent,
  loading
}) => {
  const [events, setEvents] = React.useState(initialEvents)
  const [date, setDate] = React.useState(moment())
  const [perspective, setPerspective] = React.useState('month')

  const calculatedEvents = React.useMemo(
    () => (perspective === 'month' ? events : calculateWidthAndPositionForConflicts(events)),
    [events, perspective]
  )

  let formatter = 'MM-YYYY'
  if (perspective === 'week') formatter = 'WW-' + formatter
  if (perspective === 'day') formatter = 'DD-' + formatter

  const startAt = date.clone().startOf(perspective)
  const endAt = date.clone().endOf(perspective)

  React.useEffect(() => {
    if (onChangeDate) {
      setTimeout(() => onChangeDate(date, startAt, endAt), 200)
    }
  }, [startAt.format(formatter), endAt.format(formatter)])

  useEffectAfterMount(() => {
    setEvents(initialEvents)
  }, [initialEvents])

  const daysOfMonth = React.useMemo(() => {
    if (perspective !== 'month') return []

    return SQUARES.map(index => {
      let day = index - startAt.weekday()
      if (day < 1 || day > endAt.date()) day = false
      const date = startAt.clone().add(day - 1, 'days')
      const itemEvents = events.filter(event => moment(event.date).date() === date.date())
      return { index, day, date, events: itemEvents }
    })
  }, [events, perspective])

  const daysOfWeek = React.useMemo(() => {
    if (perspective !== 'week') return []

    return range(0, 7).reduce((t, index) => {
      const date = startAt.clone().add(index, 'day')
      const itemEvents = calculatedEvents.filter(event => moment(event.date).date() === date.date())

      return [...t, { index, day: date.date(), date, events: itemEvents }]
    }, [])
  }, [events, perspective])

  const onAdd = i => {
    const tdate = date.clone().add(i, perspective)
    setEvents([])
    setDate(tdate)
  }

  const onSelectDay = date => {
    setEvents([])
    setDate(date)
    setPerspective('day')
  }

  const onSelectPerspective = perspective => {
    setEvents([])
    setPerspective(perspective)
  }

  const handleSelectEmptyTime = (hour, tDate) => {
    if (onSelectEmptyTime) {
      tDate = (tDate || date).clone().hour(hour).minute(0)
      onSelectEmptyTime(tDate)
    }
  }

  return (
    <CalendarContext.Provider
      value={{
        date,
        loading,
        perspective,
        onSelectPerspective,
        formatter,
        daysOfMonth,
        daysOfWeek,
        onAdd,
        startAt,
        endAt,
        events: calculatedEvents,
        setDate,
        onSelectDay,
        handleSelectEmptyTime,
        onSelectEvent
      }}
    >
      {children}
    </CalendarContext.Provider>
  )
}

export default CalendarHandler
